"use client"

import { type ReactNode, useCallback, useEffect, useRef, useState } from "react"

import { usePathname, useSearchParams } from "next/navigation"
import { useTranslations } from "next-intl"

import { HeaderSearch } from "@/components/marketplace/layout/header-search"
import { shortLabel } from "@/components/marketplace/layout/nav-type"
import { useBagCount } from "@/components/marketplace/layout/use-bag-count"
import { Wordmark } from "@/components/marketplace/layout/wordmark"
import type { NavCategory } from "@/components/marketplace/types"
import { Button, CloseIcon, CountBadge, HeaderBagIcon, IconButton, MenuArrowIcon, MenuChevronIcon, MenuIcon, Row, Scrim, Surface } from "@/components/ui"
import { LocalizedClientLink } from "@/components/ui/localized-client-link"
import { catalogCategoryHandle } from "@/lib/category-slugs"
import { itemAt } from "@/lib/helpers/array"
import { useRecentSearches } from "@/lib/hooks/search/use-recent-searches"
import { useClickOutside } from "@/lib/hooks/use-click-outside"
import { useCloseWhenScrolledAway } from "@/lib/hooks/use-close-when-scrolled-away"
import { useLocalizedPush } from "@/lib/hooks/use-navigation"
import { buildRoute, ROUTES, segmentsAfterRegion } from "@/lib/routes"
import { SEARCH_PARAM_KEYS } from "@/lib/types/search"
import { createConversionId } from "@/platform/observability/analytics/conversions"
import { trackSearch } from "@/platform/observability/analytics/v2/search"

const BAR_LABEL = "font-text text-copy-lg"

const MEGA_ROW = "font-text text-copy-lg flex items-center transition-colors"

const DEPARTMENT_SLOTS = ["inline-flex", "inline-flex", "inline-flex", "inline-flex", "hidden @6xl:inline-flex"]

const DepartmentLink = ({ category, active, className }: { category: NavCategory; active: boolean; className: string }) => (
  <LocalizedClientLink
    href={buildRoute.category(category.handle)}
    aria-current={active ? "page" : undefined}
    className={`press ${BAR_LABEL} min-h-11 shrink-0 items-center transition-colors ${active ? "text-fg-strong font-semibold" : "text-fg-heading hover:text-fg-strong"} ${className}`}
  >
    {shortLabel(category.label)}
  </LocalizedClientLink>
)

const linksIn = (pane: HTMLElement | null) => Array.from(pane?.querySelectorAll<HTMLAnchorElement>("a") ?? [])

const focusSibling = (pane: HTMLElement | null, current: EventTarget, step: number) => {
  const links = linksIn(pane)
  const index = links.indexOf(current as HTMLAnchorElement)
  if (index === -1) return
  links[(index + step + links.length) % links.length]?.focus()
}

const MegaMenu = ({ departments, onNavigate }: { departments: NavCategory[]; onNavigate: () => void }) => {
  const t = useTranslations("nav")
  const [active, setActive] = useState(0)
  const departmentsRef = useRef<HTMLDivElement>(null)
  const childrenRef = useRef<HTMLDivElement>(null)
  const department = departments[active] ?? departments[0]
  if (!department) return null
  const children = department.children ?? []

  const onDepartmentKeyDown = (event: React.KeyboardEvent<HTMLAnchorElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      focusSibling(departmentsRef.current, event.target, event.key === "ArrowDown" ? 1 : -1)
    }
    if (event.key === "ArrowRight") {
      event.preventDefault()
      linksIn(childrenRef.current)[0]?.focus()
    }
  }
  const onChildrenKeyDown = (event: React.KeyboardEvent<HTMLAnchorElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      focusSibling(childrenRef.current, event.target, event.key === "ArrowDown" ? 1 : -1)
    }
    if (event.key === "ArrowLeft") {
      event.preventDefault()
      linksIn(departmentsRef.current)[active]?.focus()
    }
  }

  return (
    <Surface variant="panel" radius="xl" padding="none" className="max-h-pinned flex overflow-hidden text-left">
      <div ref={departmentsRef} className="border-line-faint w-76.25 shrink-0 overflow-y-auto border-r">
        {departments.map((option, index) => {
          const activeRow = index === active
          return (
            <LocalizedClientLink
              key={option.handle}
              href={buildRoute.category(option.handle)}
              onMouseEnter={() => setActive(index)}
              onFocus={() => setActive(index)}
              onKeyDown={onDepartmentKeyDown}
              onClick={onNavigate}
              aria-current={activeRow ? "true" : undefined}
              className={`${MEGA_ROW} border-line-faint h-16 gap-375 border-b pr-450 pl-500 last:border-b-0 focus-visible:outline-none ${activeRow ? "bg-brand text-fg-inverted focus-visible:ring-fg-inverted focus-visible:ring-2 focus-visible:ring-inset" : "text-fg-heading hover:bg-line-faint/50 focus-visible:bg-line-faint/50"}`}
            >
              <span className="min-w-0 flex-1 truncate">{option.label}</span>
              {!activeRow && <MenuChevronIcon aria-hidden className="text-fg-brand shrink-0" />}
            </LocalizedClientLink>
          )
        })}
      </div>
      <div key={department.handle} ref={childrenRef} className="motion-safe:animate-fade-in-top flex min-w-0 flex-1 flex-col overflow-y-auto pt-325 pb-500">
        {children.length > 0 && (
          <ul aria-label={t("departmentChildren", { category: department.label })} className="mb-500 flex min-w-0 flex-col gap-px">
            {children.map((child) => (
              <li key={child.handle}>
                <LocalizedClientLink
                  href={buildRoute.category(child.handle)}
                  onKeyDown={onChildrenKeyDown}
                  onClick={onNavigate}
                  className={`${MEGA_ROW} text-fg-heading hover:bg-line-faint/50 focus-visible:bg-line-faint/50 h-9.5 px-500 focus-visible:outline-none`}
                >
                  <span className="min-w-0 flex-1 truncate">{child.label}</span>
                </LocalizedClientLink>
              </li>
            ))}
          </ul>
        )}
        <LocalizedClientLink
          href={buildRoute.category(department.handle)}
          onKeyDown={onChildrenKeyDown}
          onClick={onNavigate}
          className="press font-text text-copy-lg text-fg-brand hover:text-brand-hover mt-auto inline-flex shrink-0 items-center gap-200 self-center underline decoration-from-font"
        >
          {t("seeAllCategory", { category: department.label })}
          <MenuArrowIcon aria-hidden className="shrink-0" />
        </LocalizedClientLink>
      </div>
    </Surface>
  )
}

interface DesktopNavProps {
  account?: ReactNode
  bagCount?: number
  defaultMegaOpen?: boolean
  categories: NavCategory[]
}

export const DesktopNav = ({ bagCount: bagCountOverride, defaultMegaOpen = false, categories, account }: DesktopNavProps) => {
  const bagCount = useBagCount(bagCountOverride)
  const t = useTranslations("nav")
  const pathname = usePathname()
  const [section, sectionHandle] = segmentsAfterRegion(pathname)
  const activeHandle = section === ROUTES.CATEGORIES && sectionHandle ? catalogCategoryHandle(sectionHandle) : undefined
  const [megaOpen, setMegaOpen] = useState(defaultMegaOpen)
  const closeMega = useCallback(() => setMegaOpen(false), [])
  const searchParams = useSearchParams()
  // The box reflects the active query on /busqueda (and clears when the user leaves it).
  const urlQuery = searchParams.get(SEARCH_PARAM_KEYS.Q) ?? ""
  const [searchText, setSearchText] = useState(urlQuery)
  // Sync the box to the URL query during render (React's "adjusting state when
  // a prop changes" pattern) instead of one commit late via an effect.
  const [prevUrlQuery, setPrevUrlQuery] = useState(urlQuery)
  if (prevUrlQuery !== urlQuery) {
    setPrevUrlQuery(urlQuery)
    setSearchText(urlQuery)
  }
  const [searchOpen, setSearchOpen] = useState(false)
  const navRef = useRef<HTMLDivElement>(null)
  const menuTriggerRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const onSearchShortcut = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault()
        searchInputRef.current?.focus()
      }
    }
    document.addEventListener("keydown", onSearchShortcut)
    return () => document.removeEventListener("keydown", onSearchShortcut)
  }, [])

  // Outside presses close the mega menu; when it's already closed the callback
  // is a same-value setState and React bails out, so the always-on listener is
  // free. Escape stays a small menu-scoped effect.
  useClickOutside(navRef, () => setMegaOpen(false))
  useEffect(() => {
    if (!megaOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return
      setMegaOpen(false)
      menuTriggerRef.current?.querySelector("button")?.focus()
    }
    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [megaOpen])
  useCloseWhenScrolledAway(navRef, megaOpen, closeMega)
  const { add: addRecent } = useRecentSearches()
  const localizedPush = useLocalizedPush()
  const goToResults = (value: string = searchText) => {
    const term = value.trim()
    if (term.length > 0) {
      trackSearch({ search_term: term, conversionId: createConversionId() })
      addRecent(term)
    }
    localizedPush(buildRoute.search({ text: term }))
  }
  return (
    <div ref={navRef} className="relative">
      {searchOpen && !megaOpen && <Scrim label={t("closeSearch")} tone="veil" anchored onDismiss={() => setSearchOpen(false)} className="inset-x-0 top-full h-dvh" />}
      {megaOpen && <Scrim label={t("closeMenu")} tone="veil" anchored onDismiss={() => setMegaOpen(false)} className="inset-x-0 top-full h-dvh" />}
      <div className="bg-surface @container relative">
        <div className="border-line-faint border-b">
          <Row gap="500" className="px-gut max-w-page relative mx-auto h-21.25 w-full items-center">
            <LocalizedClientLink
              href="/"
              aria-label={t("brandHome")}
              className="press focus-visible:ring-brand inline-flex shrink-0 rounded-full focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
            >
              <Wordmark tone="brand" height={38} />
            </LocalizedClientLink>
            <div className="z-dropdown absolute left-1/2 w-full max-w-130.5 -translate-x-1/2">
              <HeaderSearch
                inputRef={searchInputRef}
                tone="chrome"
                size="md"
                submit
                glyph={false}
                placeholder={t("searchPlaceholder")}
                className="w-full min-w-0"
                panelClassName="z-dropdown absolute top-full left-1/2 mt-400 w-157.5 -translate-x-1/2"
                value={searchText}
                onValueChange={setSearchText}
                onSubmitTerm={goToResults}
                onOpenChange={setSearchOpen}
              />
            </div>
            <Row gap="200" className="ml-auto shrink-0 items-center">
              {account ?? <span aria-hidden className="inline-block h-10 w-29 shrink-0" />}
              <span className="relative">
                <IconButton aria-label={t("bag")} variant="ghost" className="text-fg-brand hover:text-brand-hover ring-line-faint size-10 rounded-full ring-1 ring-inset" asChild>
                  <LocalizedClientLink href={`/${ROUTES.CART}`}>
                    <HeaderBagIcon />
                  </LocalizedClientLink>
                </IconButton>
                {bagCount > 0 && <CountBadge count={bagCount} className="border-surface absolute -top-0.5 -right-0.5 h-4.5 min-w-4.5 border-2 font-extrabold" />}
              </span>
            </Row>
          </Row>
        </div>
        <Row gap="500" className="px-gut max-w-page relative mx-auto h-16 w-full items-center">
          <div ref={menuTriggerRef} className="contents">
            <Button
              variant="quiet"
              className={`press ${BAR_LABEL} text-fg-heading hover:text-fg-strong min-h-11 shrink-0 gap-250 font-normal`}
              iconLeft={<span className="text-fg-brand grid size-6 place-items-center">{megaOpen ? <CloseIcon size={20} /> : <MenuIcon size={24} />}</span>}
              onClick={() => setMegaOpen((open) => !open)}
              aria-expanded={megaOpen}
            >
              {t("allCategories")}
            </Button>
          </div>
          <Row as="nav" aria-label={t("departments")} gap="0" className="absolute left-1/2 -translate-x-1/2 items-center gap-x-500 @7xl:gap-x-600">
            {categories.slice(0, DEPARTMENT_SLOTS.length).map((category, index) => (
              <DepartmentLink key={category.handle} category={category} active={activeHandle === category.handle} className={itemAt(DEPARTMENT_SLOTS, index)} />
            ))}
          </Row>
          <Row gap="600" className="ml-auto shrink-0 items-center">
            <LocalizedClientLink
              href={buildRoute.bestSellers()}
              className={`press ${BAR_LABEL} text-fg-heading hover:text-fg-strong hidden min-h-11 shrink-0 items-center transition-colors @6xl:inline-flex`}
            >
              {t("bestSellers")}
            </LocalizedClientLink>
            <LocalizedClientLink
              href={buildRoute.offers()}
              className={`press ${BAR_LABEL} text-fg-brand hover:text-brand-hover inline-flex min-h-11 shrink-0 items-center transition-colors`}
            >
              {t("offers")}
            </LocalizedClientLink>
          </Row>
        </Row>
        {megaOpen && (
          /* px-gut and the page cap on the positioner, not the panel: absolute
             inset-x-0 resolves against the header's border box, which is full
             bleed, and that is how the menu used to hang wider than the bar's own
             content on each side. */
          <div className="sticky-chrome-wide z-dropdown px-gut max-w-page pointer-events-none absolute inset-x-0 top-full mx-auto mt-500 flex w-full justify-center">
            <div className="pointer-events-auto w-157.5 max-w-full">
              <MegaMenu departments={categories} onNavigate={() => setMegaOpen(false)} />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
